/* eslint-disable max-lines */
/* eslint-disable import/max-dependencies */
import React, { useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { StyledText } from 'src/components/StyledTypography';
import { useTranslate } from '@im/react-localization';
import Button from '@mui/material/Button';
import { BidSearchQuery, useBLCLookUpList } from '../hooks/useBLCHooks';
import { useProgramListQuery } from 'src/hooks/program-list';
import { useSearchParams } from 'react-router-dom';
import { PresetRangePicker } from '@im/ui-lib/Calendar/PresetRangePicker';
import dayjs from 'dayjs';
import { useDateTimeFormatter } from 'src/hooks/dateTimeFormatter';
import Autocomplete from '@mui/material/Autocomplete';
import Typography from '@mui/material/Typography';
import { useGetVendorList } from 'src/hooks/vendor-list';
import { useTheme } from '@mui/system';

type Props = {
  pageSize: number;
  searchQuery: BidSearchQuery;
  setSearchQuery: React.Dispatch<
    React.SetStateAction<Partial<Props['searchQuery']>>
  >;
};

const BidListSearchFields: React.FC<Props> = ({
  pageSize,
  searchQuery,
  setSearchQuery,
}) => {
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: '560px',
      },
    },
  };
  const theme = useTheme();
  const formatDateTime = useDateTimeFormatter();
  const translate = useTranslate();
  const [searchParams, setSearchParams] = useSearchParams();
  const countryCode = searchParams.get('countryCode') ?? '';
  const bidNumber = searchParams.get('bidNumber');

  const [fieldValues, setFieldValues] =
    useState<typeof searchQuery>(searchQuery);

  const vendorList = useGetVendorList();
  const bidLookUp = useBLCLookUpList();
  const programList = useProgramListQuery({
    countryCode,
    vendorIdToSearch:
      typeof fieldValues.Vendor === 'number' ? fieldValues.Vendor : 0,
  });

  const handleSearch = (
    fieldValues: typeof searchQuery,
    pagination?: {
      page: number;
      pageSize: number;
    }
  ) => {
    setSearchQuery(fieldValues);
    setFieldValues(fieldValues);
    if (fieldValues.BidNumber) {
      searchParams.set('bidNumber', fieldValues.BidNumber);
    } else {
      searchParams.delete('bidNumber');
    }
    if (pagination) {
      searchParams.set('pageSize', pagination.pageSize.toString());
      searchParams.set('pageNumber', pagination.page.toString());
    }
    setSearchParams(searchParams, { replace: true });
  };
  const handleFieldChange = (
    key: keyof typeof searchQuery,
    value: string | number
  ) => {
    setFieldValues({ ...fieldValues, [key]: value });
  };
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') handleSearch(fieldValues);
  };

  const renderSearchField = (label: string, key: keyof typeof searchQuery) => (
    <FormControl>
      <TextField
        label={label}
        name={label}
        onChange={(event) => handleFieldChange(key, event.target.value)}
        onKeyDown={handleKeyDown}
        value={fieldValues[key] ?? ''}
      />
    </FormControl>
  );
  useEffect(() => {
    if (bidNumber) {
      setSearchQuery({ ...searchQuery, BidNumber: bidNumber });
      setFieldValues({ ...fieldValues, BidNumber: bidNumber });
    } else {
      setSearchQuery({ ...searchQuery });
      setFieldValues({ ...fieldValues });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <Box>
      <Stack
        alignContent={'center'}
        direction={'row'}
        flexWrap={'wrap'}
        gap={2}
        mb={2}
        mt={2}
        sx={{
          '.MuiFormControl-root': {
            width: '256px',
          },
          '.MuiInputLabel-root': {
            fontSize: '22px !important',
          },
        }}
      >
        <FormControl>
          <Typography
            color={`${theme.palette.grey[500]} !important`}
            lineHeight={'12px'}
            variant="body1"
          >
            {translate('BidLifeCycle.ColumnVendor')}
          </Typography>
          <Autocomplete
            data-testid="vendor-autocomplete"
            getOptionLabel={(option) => option.vendorName}
            loading={vendorList.isLoading}
            onChange={(event, value) =>
              handleFieldChange('Vendor', value?.vendorID ?? '')
            }
            options={vendorList.data ?? []}
            renderInput={(params) => <TextField {...params} name="Vendor" />}
            renderOption={(renderProps, option) => (
              <MenuItem {...renderProps} dense key={option.vendorID.toString()}>
                {option.vendorName}
              </MenuItem>
            )}
            size="small"
            sx={{
              '.MuiInputBase-input': {
                paddingY: `0px !important`,
              },
            }}
            value={
              vendorList.data?.find(
                (vendor) => vendor.vendorID === fieldValues.Vendor
              ) ?? null
            }
          />
        </FormControl>
        <FormControl>
          <TextField
            SelectProps={{
              MenuProps: MenuProps,
              multiple: true,
              renderValue: (selected: any) =>
                selected
                  .map(
                    (valu: any) =>
                      programList.data?.vendorProgramlist.filter(
                        (program) => program.vendorProgramId === valu
                      )?.[0]?.vendorProgramName
                  )
                  .join(', '),
            }}
            disabled={!fieldValues.Vendor}
            label={translate('BidLifeCycle.ColumnVendorProgram')}
            name="Program"
            onChange={(event) =>
              handleFieldChange('VendorProgram', event.target.value)
            }
            select
            value={fieldValues.VendorProgram ?? []}
          >
            {programList.data?.vendorProgramlist
              .filter((program) => program.status === '1')
              .sort((a, b) =>
                a.vendorProgramName.localeCompare(b.vendorProgramName)
              )
              .map((program) => (
                <MenuItem
                  key={program.vendorProgramId}
                  value={program.vendorProgramId}
                >
                  <Checkbox
                    checked={
                      fieldValues.VendorProgram?.includes(
                        program.vendorProgramId
                      ) ?? false
                    }
                  />
                  <StyledText>{program.vendorProgramName}</StyledText>
                </MenuItem>
              ))}
          </TextField>
        </FormControl>
        <FormControl>
          <TextField
            SelectProps={{
              MenuProps: MenuProps,
              multiple: true,
              renderValue: (selected: any) =>
                selected
                  .map(
                    (valu: any) =>
                      bidLookUp.data?.value.response.statusList.filter(
                        (status) => status.statusID === valu
                      )[0].status
                  )
                  .join(', '),
            }}
            label={translate('BidLifeCycle.ColumnCurrentStatus')}
            name="Current status"
            onChange={(event) =>
              handleFieldChange('CurrentStatus', event.target.value)
            }
            select
            value={fieldValues.CurrentStatus ?? []}
          >
            {bidLookUp.data?.value.response?.statusList
              .sort((a, b) => a.status.localeCompare(b.status))
              .map((status) => (
                <MenuItem key={status.statusID} value={status.statusID}>
                  <Checkbox
                    checked={fieldValues.CurrentStatus?.includes(
                      status.statusID
                    )}
                  />
                  <StyledText>{status.status}</StyledText>
                </MenuItem>
              ))}
          </TextField>
        </FormControl>
        {renderSearchField(
          translate('BidLifeCycle.ColumnBidNumber'),
          'BidNumber'
        )}
        {renderSearchField(translate('BidLifeCycle.ColumnBCN'), 'BCN')}
        {renderSearchField(
          translate('BidLifeCycle.ColumnQuoteNumber'),
          'QuoteNumber'
        )}
        <FormControl
          sx={{
            marginTop: '10px',
          }}
        >
          <PresetRangePicker
            endDate={dayjs(fieldValues.ToDate).toDate()}
            format={(sDate, eDate) =>
              !sDate || !eDate || !fieldValues.FromDate || !fieldValues.ToDate
                ? translate('Common.DateRangePlaceholder')
                : `${formatDateTime(sDate)} to ${formatDateTime(eDate)}`
            }
            isPreset
            onClearClick={() =>
              setFieldValues((prev) => {
                delete prev.FromDate;
                delete prev.ToDate;
                return {
                  ...prev,
                };
              })
            }
            onDateChange={(startDate, endDate) => {
              setFieldValues({
                ...fieldValues,
                FromDate: dayjs(startDate).format('YYYY-MM-DD') ?? '',
                ToDate: dayjs(endDate).format('YYYY-MM-DD') ?? '',
              });
            }}
            onIntervalSelected={(dates) => {
              setFieldValues({
                ...fieldValues,
                FromDate:
                  dayjs(dates.actualStartDate).format('YYYY-MM-DD') ?? '',
                ToDate: dayjs(dates.actualEndDate).format('YYYY-MM-DD') ?? '',
              });
            }}
            startDate={dayjs(fieldValues.FromDate).toDate()}
          />
        </FormControl>
        <FormControl sx={{ marginTop: '10px', width: 'unset !important' }}>
          <Button
            onClick={() => handleSearch(fieldValues, { page: 1, pageSize })}
            startIcon={<SearchIcon />}
            variant="newContained"
          >
            {translate('Common.Search')}
          </Button>
        </FormControl>
        <FormControl sx={{ marginTop: '10px', width: 'unset !important' }}>
          <Button
            onClick={() => {
              handleSearch({}, { page: 1, pageSize });
            }}
            startIcon={<ClearIcon color="error" />}
            variant="newOutlined"
          >
            {translate('Common.ClearAll')}
          </Button>
        </FormControl>
      </Stack>
    </Box>
  );
};

export default BidListSearchFields;
